import {
  faArrowRotateRight,
  faCheck,
  faCircle,
  faCircleCheck,
  faCircleChevronRight,
  faCircleExclamation,
  faCircleInfo,
  faVideo,
  faVideoSlash,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';

import { UserRole } from '../../../store/auth/types';

export enum Status {
  // Document outcomes
  Approved = 'Approved',
  Rejected = 'Rejected',
  RejectedWithoutAlt = 'RejectedWithoutAlt',

  // Upload statuses
  Awaiting = 'Awaiting',
  UploadedError = 'UploadedError',
  UploadedOK = 'UploadedOK',
  InError = 'InError',
  Replaced = 'Replaced',
  Skipped = 'Skipped',

  // Info only statuses
  InfoOnly = 'InfoOnly',
  Acknowledged = 'Acknowledged',
  NotAcknowledged = 'NotAcknowledged',

  // Interview outcomes
  Passed = 'Passed',
  Failed = 'Failed',
  Resit = 'Resit',

  // Booked interviews
  Requested = 'Requested',
  Booked = 'Booked',
  Cancelled = 'Cancelled',

  // Recorded interviews
  Recorded = 'Recorded',
  Attempted = 'Attempted',
  Invited = 'Invited',
}

export const documentStatusIcons = {
  // Document outcome icons
  Approved: {
    icon: faCheck,
    color: 'green',
    tooltip: 'Document has been approved',
  },
  Rejected: {
    icon: faXmark,
    color: 'red',
    tooltip: 'Document has been marked as Rejected',
  },

  // Upload icons
  Uploaded: {
    icon: faCircleCheck,
    color: 'green',
    tooltip: 'Document has been uploaded',
  },
  Requested: {
    icon: faCircleChevronRight,
    color: 'cyan',
    tooltip: 'Document has been requested',
  },
  UploadError: {
    icon: faCircleExclamation,
    color: 'red',
    tooltip: 'There is an error with the document',
  },
  UploadFixed: {
    icon: faCircleCheck,
    color: 'orange',
    tooltip: 'New document has been uploaded to address previously marked issue',
  },
  UploadFixRequested: {
    icon: faCircleChevronRight,
    color: 'orange',
    tooltip: 'Uploaded document does not meet all the requirements and a new document is required',
  },
  ReplacementRequested: {
    icon: faCircleCheck,
    color: 'gray',
    tooltip: 'Uploaded document does not meet all the requirements and a new request has been generated',
  },
  NotRequired: {
    icon: faCircleCheck,
    color: 'gray',
    tooltip: 'No document is required to be uploaded',
  },

  // Info only icons
  NotAcknowledged: {
    icon: faCircleInfo,
    color: 'cyan',
    tooltip: 'Request has not been acknowledged',
  },
  Acknowledged: {
    icon: faCheck,
    color: 'green',
    tooltip: 'Request has been acknowledged',
  },

  // Interview outcome icons
  InterviewPassed: {
    icon: faCheck,
    color: 'green',
    tooltip: 'Interview has been marked as Passed',
  },
  InterviewResit: {
    icon: faArrowRotateRight,
    color: 'orange',
    tooltip: 'Interview has been marked as Resit',
  },
  InterviewFailed: {
    icon: faXmark,
    color: 'red',
    tooltip: 'Interview has been marked as Failed',
  },
  InterviewNoActionRequired: {
    icon: faCircleCheck,
    color: 'gray',
    tooltip: 'No further interview action is required',
  },

  // Booked interview icons
  BookedInterviewRequested: {
    icon: faCircleChevronRight,
    color: 'cyan',
    tooltip: 'Interview booking has been requested',
  },
  BookedInterviewBooked: {
    icon: faCircleCheck,
    color: 'green',
    tooltip: 'Interview request has been booked',
  },
  BookedInterviewCancelled: {
    icon: faCircle,
    color: 'gray',
    tooltip: 'Interview has been cancelled',
  },

  // Recorded interview icons
  RecordedInterviewInvited: {
    icon: faCircleChevronRight,
    color: 'cyan',
    tooltip: 'Student has been invited to the interview',
  },
  RecordedInterviewRecorded: {
    icon: faVideo,
    color: 'green',
    tooltip: 'Interview has been recorded',
  },
  RecordedInterviewAttempted: {
    icon: faVideoSlash,
    color: 'orange',
    tooltip: 'Interview has been attempted but not completed',
  },
};

export interface Props extends React.HTMLProps<HTMLDivElement> {
  id?: string;
  status: Status;
  isDocumentFixed?: boolean;
  isDeleting?: boolean;
  isDocSetup?: boolean;
  reason?: string;
  description?: string;
  label: string;
  labelBtn?: React.ReactNode;
  isExpanded?: boolean;
  canCheckOff?: boolean;
  isCheckedOff?: boolean;
  onCheckOffRisk?: () => void;
  onClick?: () => void;
  onDeleteClick?: () => void;
  children?: React.ReactNode;
  userRole?: UserRole;
  deletedByTTL?: boolean;
}
